import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./imageUploader.html";
import { IS_DEVELOPMENT, MAX_ASSET_UPLOAD_SIZE } from '~/config';
import { uploadFile } from '~/modules/services/assetProxyService';
import { uploadFileToRLM } from '~/modules/services/rlmService';
import { uploadFile as secureUploadFile } from '~/modules/services/vaultService';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { confirmPrompt } from '~/modules/utilities/browserUtilities';
import { classSet } from '~/modules/utilities/cssClassUtilities';
import { throwDevelopmentOrLog } from '~/modules/utilities/errorUtilities';
import { formatBytes, isValidFormat } from '~/modules/utilities/fileUtilities';
import { notifyFailure, notifySuccess } from '~/modules/utilities/notificationUtilities';
import imageUploaderTemplate from './imageUploader.html';
import imageUploaderSlimTemplate from './imageUploaderSlim.html';
const MAX_PREVIEW_WIDTH = 160;
const CARD_VARIANT = 'card';
const SLIM_VARIANT = 'slim';
export let UploadFailedTypes = /*#__PURE__*/function (UploadFailedTypes) {
  UploadFailedTypes[UploadFailedTypes["InvalidFileFormat"] = 0] = "InvalidFileFormat";
  UploadFailedTypes[UploadFailedTypes["FileTooBig"] = 1] = "FileTooBig";
  UploadFailedTypes[UploadFailedTypes["FileMissingExtension"] = 2] = "FileMissingExtension";
  UploadFailedTypes[UploadFailedTypes["Unknown"] = 3] = "Unknown";
  return UploadFailedTypes;
}({});
class ImageUploader extends LightningElement {
  constructor(...args) {
    super(...args);
    this.useSecureStorage = false;
    this.variant = CARD_VARIANT;
    this.helpText = void 0;
    this.helpTextTitle = void 0;
    this.ariaLabel = void 0;
    this.previewImageUrl = void 0;
    this.previewImageWidth = void 0;
    this.previewImageHeight = void 0;
    this.supportedFormats = void 0;
    this.uploadStarted = void 0;
    this.uploadFailed = void 0;
    this.changeUrl = void 0;
    this.showHelpText = false;
    this.index = void 0;
    this.disabled = false;
    this.required = false;
    this.forceShowError = false;
    this.requiredText = 'This field is required';
    this.objectName = '';
    this.maxAssetUploadSize = MAX_ASSET_UPLOAD_SIZE;
    this.successMessage = void 0;
    this.supressSuccessMessage = false;
    this.tooLargeErrorMessage = void 0;
    this.uploadFailedMessage = void 0;
    this.uploadImageMessage = void 0;
    this.slimDropText = void 0;
    this.inlineErrorMessage = '';
    this.useRlmStorage = false;
    this.inputValue = '';
    this.labels = {};
    this.sharedLabels = {};
    this.uploadProgress = 0;
    this._isUploading = false;
    this.hasUploadFailed = false;
    this.handleFocus = () => {
      const dragArea = this.template.querySelector('.drop-area');
      if (dragArea) {
        dragArea.classList.add('highlight');
      }
    };
    this.handleBlur = () => {
      const dragArea = this.template.querySelector('.drop-area');
      if (dragArea) {
        dragArea.classList.remove('highlight');
      }
      const uploadButton = this.template.querySelector('.upload-button');
      if (uploadButton) {
        uploadButton.blur();
      }
    };
    this.clickRemove = () => {
      this.changeUrl('', '', this.index);
    };
  }
  focus() {
    if (this.variant === SLIM_VARIANT) {
      if (this.previewImageUrl) {
        const closeButton = this.template.querySelector('c360-icon');
        closeButton === null || closeButton === void 0 ? void 0 : closeButton.focus();
      } else {
        const uploadButton = this.template.querySelector('c360-button');
        uploadButton === null || uploadButton === void 0 ? void 0 : uploadButton.focus();
      }
    }
  }
  i18nConnected(i18n) {
    this.labels = i18n.imageUploader;
    this.sharedLabels = i18n.shared;
  }
  get isUploading() {
    return this._isUploading;
  }
  set isUploading(value) {
    if (value) {
      this.uploadProgress = 0;
      this.hasUploadFailed = false;
    }
    this._isUploading = value;
  }
  get uploadProgressLabel() {
    return i18nStore.getState().i18n.listingMediaStep.uploadingLabel({
      UPLOAD_PROGRESS: this.uploadProgress
    });
  }
  get acceptedFormats() {
    if (this.supportedFormats) {
      const result = this.supportedFormats.reduce((accumulator, current) => {
        return `${accumulator ? `${accumulator},` : ''}.${current}`;
      }, '');
      return result;
    }
    return 'image/*';
  }
  get uploadImageLabel() {
    var _this$uploadImageMess, _this$uploadImageMess2;
    return (_this$uploadImageMess = (_this$uploadImageMess2 = this.uploadImageMessage) === null || _this$uploadImageMess2 === void 0 ? void 0 : _this$uploadImageMess2.call(this)) !== null && _this$uploadImageMess !== void 0 ? _this$uploadImageMess : this.labels.uploadImage;
  }
  connectedCallback() {
    if (!this.changeUrl && IS_DEVELOPMENT) {
      throw new Error("ImageUploader component must have 'changeUrl' property.");
    }
  }
  render() {
    if (this.variant === CARD_VARIANT) {
      return imageUploaderTemplate;
    }
    return imageUploaderSlimTemplate;
  }
  handleDrag(event) {
    this.preventDefault(event);
    if (this.disabled) {
      return;
    }
    const dragArea = this.template.querySelector('.drop-area');
    if (dragArea) {
      this.hasUploadFailed = false;
      dragArea.classList.add('highlight');
    }
  }
  handleDragLeave(event) {
    this.preventDefault(event);
    const dragArea = this.template.querySelector('.drop-area');
    if (dragArea) {
      dragArea.classList.remove('highlight');
    }
  }
  handleDrop(event) {
    if (this.disabled) {
      throwDevelopmentOrLog(new Error('FileUploader is disabled.'));
      return;
    }
    this.preventDefault(event);
    const dragArea = this.template.querySelector('.drop-area');
    if (dragArea) {
      dragArea.classList.remove('highlight');
    }
    const files = [];
    try {
      // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop
      if (event.dataTransfer.items) {
        // Use DataTransferItemList interface to access the file(s)
        for (const item of event.dataTransfer.items) {
          // If dropped items aren't files, reject them
          if (item.kind === 'file' && isValidFormat(this.supportedFormats, item.getAsFile().name)) {
            files.push(item.getAsFile());
          } else {
            throw new Error('invalid_format');
          }
        }
      } else {
        // Use DataTransfer interface to access the file(s)
        for (const file of event.dataTransfer.files) {
          if (isValidFormat(this.supportedFormats, file.getAsFile().name)) {
            files.push(file);
          } else {
            throw new Error('invalid_format');
          }
        }
      }
      this.uploadFile(files);
    } catch (error) {
      if (error.message === 'missing_extension') {
        this.handleFailure(this.labels.fileWithoutExtension, UploadFailedTypes.FileMissingExtension);
      } else if (error.message === 'invalid_format') {
        this.handleFailure(this.labels.invalidFileFormat, UploadFailedTypes.InvalidFileFormat);
      } else {
        this.handleFailure(this.uploadFailedMessage({
          FILE_NAME: files
        }), UploadFailedTypes.Unknown);
      }
    }
  }
  preventDefault(event) {
    event.preventDefault();
    event.stopPropagation();
  }
  clickUpload(event) {
    if (this.disabled) {
      throwDevelopmentOrLog(new Error('FileUploader is disabled.'));
      return;
    }
    this.hasUploadFailed = false;
    const fileInput = this.template.querySelector('.file-input');
    fileInput.click();
    event.stopPropagation();
  }
  async clickDelete(event) {
    event.preventDefault();
    event.stopPropagation();
    if (await confirmPrompt(this.sharedLabels.confirmDelete)) {
      this.changeUrl('', '', this.index);
    }
  }
  handleUpload(event) {
    if (this.disabled) {
      throwDevelopmentOrLog(new Error('FileUploader is disabled.'));
      return;
    }
    const target = event.target;
    const files = target.files;
    const fileName = target.value;
    try {
      if (isValidFormat(this.supportedFormats, fileName)) {
        this.uploadFile(files);
      } else {
        this.handleFailure(this.labels.invalidFileFormat);
      }
    } catch (error) {
      if (error.message === 'missing_extension') {
        this.handleFailure(this.labels.fileWithoutExtension);
      } else {
        this.handleFailure(this.uploadFailedMessage({
          FILE_NAME: fileName
        }));
      }
    }
  }
  async uploadFile(files, _objectId) {
    if (this.disabled) {
      throwDevelopmentOrLog(new Error('FileUploader is disabled.'));
      return;
    }
    if (!files || files.length !== 1) {
      throwDevelopmentOrLog(new Error('FileUploader component can only upload one file at a time.'));
      return;
    }

    // order matters here, fileUploadStarted should always be called before fileUploadFailed
    if (this.uploadStarted) {
      this.uploadStarted(Array.from(files).map(file => file.name));
    }
    let fileName = files[0].name;
    if (fileName.length >= 20) {
      fileName = files[0].name.slice(0, 13) + '...' + files[0].name.split('.').pop();
    }
    if (files[0].size > this.maxAssetUploadSize) {
      this.handleFailure(this.tooLargeErrorMessage({
        FILE_NAME: fileName,
        NAME: this.objectName,
        MAX_SIZE: formatBytes(this.maxAssetUploadSize)
      }), UploadFailedTypes.FileTooBig);
      return;
    }
    this.isUploading = true;
    try {
      let url;
      if (this.useSecureStorage) {
        url = await secureUploadFile(files[0], percentCompleted => this.uploadProgress = percentCompleted);
      } else if (this.useRlmStorage) {
        url = await uploadFileToRLM(files[0], this.objectName, percentCompleted => this.uploadProgress = percentCompleted);
      } else {
        url = await uploadFile(files[0], percentCompleted => this.uploadProgress = percentCompleted);
      }
      if (!url) {
        throw new Error();
      }
      if (this.changeUrl) {
        this.changeUrl(url, files[0].name, this.index);
        if (!this.supressSuccessMessage) {
          notifySuccess(this.successMessage({
            FILE_NAME: fileName,
            NAME: this.objectName
          }));
        }
      }
    } catch {
      this.handleFailure(this.uploadFailedMessage({
        FILE_NAME: fileName
      }), UploadFailedTypes.Unknown);
    }
    this._isUploading = false;
  }
  handleFailure(notifyText, failType) {
    this.hasUploadFailed = true;
    this.handleBlur();
    notifyFailure(notifyText);
    if (this.uploadFailed) {
      this.uploadFailed(failType);
    }
    this._isUploading = false;
  }
  get computedCss() {
    if (this.showHelpText) {
      return 'slds-size_3-of-4 slds-m-bottom_small';
    }
    return 'slds-size_4-of-4 slds-m-bottom_small';
  }
  get computedStyle() {
    return `max-width: ${Math.min(parseInt(this.previewImageWidth), MAX_PREVIEW_WIDTH)}px;`;
  }
  get showRequiredError() {
    return this.required && this.forceShowError && this.previewImageUrl === '';
  }
  get dropAreaClass() {
    const dropAreaClassSet = classSet('drop-area');
    if (this.disabled) {
      dropAreaClassSet.add('drop-area-disabled');
    }
    if (this.hasUploadFailed) {
      dropAreaClassSet.add('upload-failed');
    }
    return dropAreaClassSet;
  }
  get dropTextClass() {
    const _class = 'drop-text';
    return this.disabled ? _class + ' drop-text-disabled' : _class;
  }
  get computedSlimDropText() {
    var _this$slimDropText;
    return (_this$slimDropText = this.slimDropText) !== null && _this$slimDropText !== void 0 ? _this$slimDropText : this.labels.slimDropText;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(ImageUploader, {
  publicProps: {
    useSecureStorage: {
      config: 0
    },
    variant: {
      config: 0
    },
    helpText: {
      config: 0
    },
    helpTextTitle: {
      config: 0
    },
    ariaLabel: {
      config: 0
    },
    previewImageUrl: {
      config: 0
    },
    previewImageWidth: {
      config: 0
    },
    previewImageHeight: {
      config: 0
    },
    supportedFormats: {
      config: 0
    },
    uploadStarted: {
      config: 0
    },
    uploadFailed: {
      config: 0
    },
    changeUrl: {
      config: 0
    },
    showHelpText: {
      config: 0
    },
    index: {
      config: 0
    },
    disabled: {
      config: 0
    },
    required: {
      config: 0
    },
    forceShowError: {
      config: 0
    },
    requiredText: {
      config: 0
    },
    objectName: {
      config: 0
    },
    maxAssetUploadSize: {
      config: 0
    },
    successMessage: {
      config: 0
    },
    supressSuccessMessage: {
      config: 0
    },
    tooLargeErrorMessage: {
      config: 0
    },
    uploadFailedMessage: {
      config: 0
    },
    uploadImageMessage: {
      config: 0
    },
    slimDropText: {
      config: 0
    },
    inlineErrorMessage: {
      config: 0
    },
    useRlmStorage: {
      config: 0
    }
  },
  publicMethods: ["focus"],
  wire: {
    i18nConnected: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["inputValue", "labels", "sharedLabels", "uploadProgress", "_isUploading", "hasUploadFailed", "handleFocus", "handleBlur", "clickRemove"]
});
const __lwc_component_class_internal = _registerComponent(ImageUploader, {
  tmpl: _tmpl,
  sel: "appx-image-uploader",
  apiVersion: 62
});
export default __lwc_component_class_internal;